import axios from "axios";

import myConfig from "../../configs/config";
import {
  fetchGroupFarms,
  fetchGroupLandPlots,
  normalizeListResponse,
} from "../PendingSchedules/serviceOrderLocationApi";

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem("client-token")}`,
  };
}

export async function fetchSectionReportMachines(farmId) {
  const response = await axios.get(`${myConfig.API_URL}/equipment/`, {
    params: { farm_id: farmId },
    headers: authHeaders(),
  });
  return normalizeListResponse(response.data);
}

export async function fetchSectionMapCatalog(farmId) {
  try {
    const response = await axios.get(
      `${myConfig.API_URL}/equipment/section_map_catalog/`,
      {
        params: { farm_id: farmId },
        headers: authHeaders(),
      }
    );
    return response.data || { sub_farms: [], landplots: [] };
  } catch (error) {
    return fetchSectionMapCatalogFallback(farmId);
  }
}

async function fetchSectionMapCatalogFallback(farmId) {
  const [subFarms, landplots] = await Promise.all([
    fetchGroupFarms(farmId),
    fetchGroupLandPlots(farmId),
  ]);

  const landplotCountByFarm = {};
  landplots.forEach((plot) => {
    const farmKey = String(plot.farm || plot.farm_id || "");
    if (!farmKey) return;
    landplotCountByFarm[farmKey] = (landplotCountByFarm[farmKey] || 0) + 1;
  });

  return {
    sub_farms: subFarms.map((farm) => ({
      id: farm.id,
      name: farm.nome_da_fazenda || farm.name || "",
      has_boundary: Boolean(farm.boundary || farm.has_boundary),
      landplot_count: landplotCountByFarm[String(farm.id)] || 0,
    })),
    landplots: landplots.map((plot) => ({
      id: plot.id,
      name: plot.name || "",
      farm_id: plot.farm || plot.farm_id,
      farm_name: plot.farm_name || "",
      area_h2: Number(String(plot.area_h2 || "0").replace(",", ".")) || 0,
      hole_count: 0,
      has_geofence: Boolean(plot.geofence_id),
      has_geometry: Boolean(plot.area || plot.pipeCoords),
    })),
  };
}

export async function fetchSectionReportOperators(farmId) {
  const response = await axios.get(`${myConfig.API_URL}/operator/`, {
    params: { farm_id: farmId },
    headers: authHeaders(),
  });
  return normalizeListResponse(response.data);
}

export function toggleSelectionId(list, id) {
  const numericId = Number(id);
  return list.includes(numericId)
    ? list.filter((item) => item !== numericId)
    : list.concat(numericId);
}

export function toggleAllSelectionIds(currentIds, allIds) {
  if (!allIds || !allIds.length) {
    return [];
  }
  const normalized = allIds.map((id) => Number(id));
  const allSelected =
    normalized.length > 0 &&
    normalized.every((id) => currentIds.includes(id));
  return allSelected ? [] : normalized;
}

export function hasSectionSelection(subFarmIds, landplotIds) {
  return (
    (subFarmIds && subFarmIds.length > 0) ||
    (landplotIds && landplotIds.length > 0)
  );
}

function appendInsideParams(params, insideOnly) {
  params.set("in_ldp", insideOnly ? "true" : "true");
  params.set("out_ldp", insideOnly ? "false" : "true");
}

export function buildSectionWorkReportUrl({
  farmId,
  machineIds,
  subFarmIds = [],
  landplotIds = [],
  operatorIds = [],
  dtIni,
  dtEnd,
  insideOnly = true,
  format = "json",
}) {
  const params = new URLSearchParams({
    farm_id: farmId,
    eq: machineIds.join(","),
    dt_ini: dtIni,
    dt_end: dtEnd,
    format,
  });
  if (subFarmIds.length > 0) {
    params.set("sub_farms", subFarmIds.join(","));
  }
  if (landplotIds.length > 0) {
    params.set("ldps", landplotIds.join(","));
  }
  if (operatorIds.length > 0) {
    params.set("operators", operatorIds.join(","));
  }
  appendInsideParams(params, insideOnly);
  return `${myConfig.API_URL}/equipment/section_work_report/?${params.toString()}`;
}

export function buildSectionWorkReportExportUrl(query) {
  return buildSectionWorkReportUrl({ ...query, format: "xls" });
}

export async function fetchSectionWorkReport(params) {
  const url = buildSectionWorkReportUrl(params);
  const response = await axios.get(url, { headers: authHeaders() });
  return normalizeListResponse(response.data);
}

export function groupRowsBySection(rows) {
  const groups = new Map();
  (rows || []).forEach((row) => {
    const key = row.section_name || row.land_plot || "—";
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(row);
  });
  return Array.from(groups.entries()).map(([sectionName, sectionRows]) => ({
    sectionName,
    rows: sectionRows,
  }));
}

export function fenceSourceLabel(t, fenceSource, holeCount) {
  if (fenceSource === "map_subfarm") {
    return t("Perímetro da sub-fazenda");
  }
  if (fenceSource === "map_recorte" || holeCount > 0) {
    return t("Cerca com recortes");
  }
  if (fenceSource === "map_cerca") {
    return t("Cerca virtual");
  }
  return t("Talhão no mapa");
}

export function summarizeSectionFilters(catalog, subFarmIds, landplotIds) {
  const subFarms = (catalog && catalog.sub_farms) || [];
  const landplots = (catalog && catalog.landplots) || [];
  const subFarmNames = subFarms
    .filter((farm) => subFarmIds.includes(Number(farm.id)))
    .map((farm) => farm.name);
  const selectedPlots = landplots.filter((plot) =>
    landplotIds.includes(Number(plot.id))
  );
  const recorteCount = selectedPlots.filter((plot) => plot.hole_count > 0).length;
  return {
    subFarmCount: subFarmIds.length,
    landplotCount: landplotIds.length,
    subFarmNames,
    recorteCount,
  };
}
